import {
  type ExtractedClaim,
  FACTCHECK_EXCERPT_CHARS,
  FACTCHECK_SECTION_EXCERPT_CHARS,
  MIN_QUOTE_CHARS,
  type StatuteRef,
  chapterExcerpt,
  compareSections,
  hasStatuteHeadings,
  isStatuteLink,
  normalizePageUrl,
  quoteIsInExcerpt,
  sectionExcerpt,
  stripLinks,
} from './article-factcheck';
import { allocate, cutSection } from './article-source-window';

/**
 * ONE EVIDENCE STORE PER ARTICLE REVISION — the pure half.
 *
 * An article is judged by three readers: the readiness check (`claims_found_in_sources`), the editorial
 * critique and the claim check. Each used to fetch its own pages. A statute section one of them read was
 * invisible to the others (the claim check read § 22.01 and § 22.23 while readiness failed "not in any text
 * Mora read"; the critique's retrieval of § 151.359 returned the site's navigation shell while the claim
 * check read the same section). Every page or section any of them fetches is now one row of
 * `article_evidence_pages`, keyed by (topic, content hash, requested url), and a reader takes what is there
 * and fetches only what is missing. This file is the part with no I/O: how a row is shaped, which rows are
 * reusable, how a statute section is found inside a stored chapter page, and how the writer's own claims
 * ledger is verified against stored page text. `evidence-store.ts` reads and writes the table.
 */

export type EvidenceKind = 'statute' | 'source';

export interface EvidencePage {
  /** The address that was asked for, fragment included for a statute section. The row's key. */
  url: string;
  canonicalUrl: string | null;
  title: string | null;
  /** The section an anchored statute url asked for (`22.01`); null for any other page. */
  sectionId: string | null;
  kind: EvidenceKind;
  /** The retained text (at most 24,000 characters); null when the page could not be read. */
  text: string | null;
  truncated: boolean;
  ok: boolean;
  /** Why it could not be read; null when it was. */
  limitation: string | null;
  /** Asking again could help (a rate limit, an empty render). A refusal and a missing page are final. */
  retryable: boolean;
  fetchedAt: string;
  fetchedByJob: string | null;
}

export function evidenceKindOf(url: string): EvidenceKind {
  return isStatuteLink(url) ? 'statute' : 'source';
}

export function sectionIdOfUrl(url: string): string | null {
  const fragment = url.split('#')[1];
  return fragment && /^\d+[A-Za-z]?(?:\.\d+[A-Za-z]?)+$/.test(fragment) ? fragment : null;
}

/** The key two requests share when they ask for the same page: a source ignores its fragment, a statute section keeps it. */
export function evidenceKey(url: string): string {
  const base = normalizePageUrl(url.split('#')[0] ?? url);
  return evidenceKindOf(url) === 'statute' ? `${base}#${sectionIdOfUrl(url) ?? ''}` : base;
}

/** `TX.22` for `…/Docs/TX/htm/TX.22.htm`; null for anything else. */
export function statuteChapterKey(url: string): string | null {
  const match = /\/Docs\/([A-Z]{2})\/htm\/\1\.(\d+[A-Za-z]?)\.htm/i.exec(url);
  return match ? `${match[1]!.toUpperCase()}.${match[2]!.toUpperCase()}` : null;
}

/**
 * A failure that asking again will not fix: a publisher's refusal, a missing page. A rate limit, an empty
 * render, a navigation shell and a timeout can be fixed by another try, so a later judge is allowed to ask
 * again; these are not.
 */
export function isPermanentLimitation(limitation: string | null | undefined): boolean {
  return /HTTP 40[134]|HTTP 410|blocked automated retrieval|not found|no longer exists|forbidden/i.test(
    limitation ?? '',
  );
}

/**
 * A failure that says nothing about the page: the job ran out of fetches, or its lease was lost. Never
 * stored, so the next judge asks.
 */
export function isTransientJobFailure(error: string | null | undefined): boolean {
  return /request limit reached|lease lost|not configured|invalid text receipt|was not requested/i.test(
    error ?? '',
  );
}

export interface EvidenceIndex {
  pages: readonly EvidencePage[];
  byKey: ReadonlyMap<string, EvidencePage>;
}

export function indexEvidence(pages: readonly EvidencePage[]): EvidenceIndex {
  const byKey = new Map<string, EvidencePage>();
  for (const page of pages) {
    const keys = [evidenceKey(page.url)];
    if (page.canonicalUrl && page.kind === 'source') keys.push(evidenceKey(page.canonicalUrl));
    for (const key of keys) {
      const existing = byKey.get(key);
      // A page that was read beats one that was not, and a later read beats an earlier one.
      if (
        !existing ||
        (page.ok && !existing.ok) ||
        (page.ok === existing.ok && page.fetchedAt > existing.fetchedAt)
      )
        byKey.set(key, page);
    }
  }
  return { pages, byKey };
}

/** The stored row for a url, or null when it was never asked for. */
export function lookupEvidence(index: EvidenceIndex, url: string): EvidencePage | null {
  return index.byKey.get(evidenceKey(url)) ?? null;
}

/**
 * Whether a stored row answers a request without another fetch: a page that was read, or one that failed in a
 * way asking again cannot fix.
 */
export function evidenceAnswers(page: EvidencePage | null | undefined): page is EvidencePage {
  if (!page) return false;
  if (page.ok) return Boolean(page.text?.trim());
  return !page.retryable || isPermanentLimitation(page.limitation);
}

/**
 * The most of one page the store keeps (`article_evidence_pages.page_text`): 120,000 characters of a long
 * request (review and claim check), 24,000 of a row written before that. A page the retrieval cut at its end
 * says so in `truncated`; a statute window that stops there ends on a section boundary.
 */
export const EVIDENCE_PAGE_MAX_CHARS = 120_000;

export interface StatuteSectionRead {
  /** The section's text, `Sec. N.` heading to the next heading, links stripped. */
  excerpt: string;
  /** The stored page it was read from. */
  page: EvidencePage;
  /**
   * False when the section runs to the last character of a page that was itself cut short: the page
   * ends where the retained window ended, not where the section does. Such a read stops mid-section
   * (§ 41.43 read as 3,889 characters of 5,817, and the reviewer said the statute "cuts off before (d)").
   */
  complete: boolean;
  /** The section is longer than the one canonical read keeps. */
  truncated: boolean;
}

/**
 * A page is cut when its retrieval says text after the window was dropped. A row written before the long
 * window existed says it for any window that left something out of either end, so it is read as cut too.
 */
const pageIsCut = (page: EvidencePage): boolean => page.truncated;

/**
 * THE one read of a statute section from the store, for every judge. The section is found on any stored page
 * of its chapter that holds its `Sec. N.` heading. A page that holds it WHOLE beats one that stops inside it,
 * and a page fetched anchored at the section beats one that merely contains it, so two judges asking for the
 * same section of the same revision get the same characters. When only a stopped-short read exists it is
 * returned, flagged `complete: false`, for a caller that cannot fetch to use and to say so.
 */
export function readStatuteSection(
  pages: readonly EvidencePage[],
  chapterKey: string,
  section: string,
  maxChars: number = FACTCHECK_SECTION_EXCERPT_CHARS,
): StatuteSectionRead | null {
  const candidates = pages
    .filter(
      (page) =>
        page.ok &&
        page.kind === 'statute' &&
        page.text &&
        statuteChapterKey(page.url) === chapterKey &&
        hasStatuteHeadings(page.text),
    )
    .sort(
      (a, b) =>
        Number(b.sectionId === section) - Number(a.sectionId === section) ||
        (a.fetchedAt < b.fetchedAt ? 1 : a.fetchedAt > b.fetchedAt ? -1 : 0),
    );
  let partial: StatuteSectionRead | null = null;
  for (const page of candidates) {
    const found = sectionExcerpt(page.text!, section, maxChars);
    if (!found) continue;
    const read: StatuteSectionRead = {
      excerpt: found.excerpt,
      page,
      complete: !(found.reachedEnd && pageIsCut(page)),
      truncated: found.truncated,
    };
    if (read.complete) return read;
    if (!partial || read.excerpt.length > partial.excerpt.length) partial = read;
  }
  return partial;
}

/**
 * A statute section already in the store, found on ANY stored page of the same chapter, whatever anchor it
 * was fetched with: a window that starts at § 22.01 also holds § 22.23 when the chapter is short, and a
 * page fetched for one judge serves the next. A page that holds the section only in part (the window ended
 * inside it) does not answer: asking for the section again fetches it whole.
 */
export function findStatutePageFor(index: EvidenceIndex, url: string): EvidencePage | null {
  const exact = lookupEvidence(index, url);
  const section = sectionIdOfUrl(url);
  const chapter = statuteChapterKey(url);
  if (!section || !chapter) return exact && evidenceAnswers(exact) ? exact : null;
  if (evidenceAnswers(exact) && exact.ok) {
    const read = readStatuteSection([exact], chapter, section);
    if (!read || read.complete) return exact;
  }
  const read = readStatuteSection(index.pages, chapter, section);
  if (read?.complete) return read.page;
  if (read) return null;
  return exact && evidenceAnswers(exact) && !exact.ok ? exact : null;
}

export interface StatuteEvidenceText {
  /** The statute sections concatenated, each from its own `Sec. N.` heading to the next heading. */
  text: string;
  /** Sections found, ascending. */
  sections: string[];
  /** Whole chapters an article cites with no section, whose opening is in `text`. */
  chapters: string[];
  /** Cited sections the store does not hold. */
  missing: string[];
  /** Sections whose only stored read stops inside the section, where the stored page was cut. */
  partial: string[];
  /** Sections shown as their opening plus the subsections the article cites, because the whole did not fit. */
  trimmed: string[];
}

/**
 * The most statute text one review is shown or retains. Every cited section is shown in full below this; above
 * it each section is cut to its opening plus the subsections the article cites, never dropped.
 */
export const STATUTE_EVIDENCE_MAX_CHARS = 80_000;
/** Below this a section's share of the room holds too little of it to count as read. */
const MIN_SECTION_SHOWN_CHARS = 600;

/**
 * The text of the statute sections an article cites, taken from the store. This is what the readiness check
 * counts as "read" and what the editorial critique is shown of the law: the same sections, from the same
 * canonical read (`readStatuteSection`), the claim check judged against, so the three agree about what was read.
 *
 * `subsections` (section number to the subsection letters the article names, from `citedSubsections`) says
 * where to cut a section that cannot be shown whole in the room left.
 */
export function statuteEvidenceText(
  index: EvidenceIndex,
  refs: readonly Pick<StatuteRef, 'code' | 'chapter' | 'section'>[],
  maxChars: number = STATUTE_EVIDENCE_MAX_CHARS,
  subsections: ReadonlyMap<string, readonly string[]> = new Map(),
): StatuteEvidenceText {
  const wanted = new Map<string, { code: string; chapter: string; section: string }>();
  for (const ref of refs)
    if (ref.code && ref.section)
      wanted.set(`${ref.code}.${ref.section}`, {
        code: ref.code,
        chapter: ref.chapter,
        section: ref.section,
      });
  const ordered = [...wanted.values()].sort(
    (a, b) =>
      a.code.localeCompare(b.code) ||
      parseInt(a.chapter, 10) - parseInt(b.chapter, 10) ||
      compareSections(a.section, b.section),
  );
  const reads: { section: string; excerpt: string; complete: boolean }[] = [];
  const missing: string[] = [];
  const chapters: string[] = [];
  for (const ref of ordered) {
    const read = readStatuteSection(
      index.pages,
      `${ref.code}.${ref.chapter}`.toUpperCase(),
      ref.section,
    );
    if (!read) missing.push(ref.section);
    else reads.push({ section: ref.section, excerpt: read.excerpt, complete: read.complete });
  }
  const gaps = Math.max(0, reads.length - 1) * 2;
  const total = reads.reduce((sum, read) => sum + read.excerpt.length, 0) + gaps;
  const trimmed: string[] = [];
  let kept = reads;
  let shown = reads.map((read) => read.excerpt);
  if (total > maxChars) {
    const caps = allocate(
      reads.map((read) => read.excerpt.length),
      Math.max(0, maxChars - gaps),
    );
    // A share too small to hold anything of a section is not a read of it: the section is reported missing.
    kept = reads.filter((read, at) => {
      const enough = caps[at]! >= Math.min(MIN_SECTION_SHOWN_CHARS, read.excerpt.length);
      if (!enough) missing.push(read.section);
      return enough;
    });
    shown = kept.map((read) => {
      const cap = caps[reads.indexOf(read)]!;
      const cut = cutSection(read.excerpt, subsections.get(read.section) ?? [], cap);
      if (cut.length < read.excerpt.length) trimmed.push(read.section);
      return cut;
    });
  }
  const parts = [...shown];
  let used = shown.reduce((sum, part) => sum + part.length, 0) + gaps;
  // A chapter cited as a whole ("Tax Code Chapter 311") has no section to cut out. The chapter page the claim
  // check read is evidence that the chapter was read: its opening goes in, so "Chapter 311" is a citation
  // Mora read and not one it supplied from memory. A chapter whose page is not stored is not reported missing:
  // it informs, it never gates.
  const wantedChapters = new Map<string, { code: string; chapter: string }>();
  for (const ref of refs)
    if (ref.code && !ref.section)
      wantedChapters.set(`${ref.code}.${ref.chapter}`.toUpperCase(), {
        code: ref.code,
        chapter: ref.chapter,
      });
  for (const [chapterKey, ref] of [...wantedChapters].sort(([a], [b]) => a.localeCompare(b))) {
    const page = index.pages.find(
      (candidate) =>
        candidate.ok &&
        candidate.kind === 'statute' &&
        candidate.text &&
        hasStatuteHeadings(candidate.text) &&
        statuteChapterKey(candidate.url) === chapterKey,
    );
    const opening = page?.text ? chapterExcerpt(page.text, FACTCHECK_EXCERPT_CHARS) : null;
    if (!opening || used + opening.excerpt.length > maxChars) continue;
    const labelled = new RegExp(`\\bchapter\\s+${ref.chapter}\\b`, 'i').test(opening.excerpt)
      ? opening.excerpt
      : `Chapter ${ref.chapter}.\n${opening.excerpt}`;
    parts.push(labelled);
    chapters.push(ref.chapter);
    used += labelled.length + 2;
  }
  return {
    text: parts.join('\n\n'),
    sections: kept.map((read) => read.section),
    chapters,
    missing,
    partial: kept.filter((read) => !read.complete).map((read) => read.section),
    trimmed,
  };
}

/**
 * Statute sections and whole chapters the article cites (never the ones a checker chose), for what a review
 * must have read. A chapter-only citation is a citation: the readiness check counts "Chapter 311" as read
 * only when text of that chapter is in what was read.
 */
export function citedStatuteRefs(claims: readonly ExtractedClaim[]): StatuteRef[] {
  const found = new Map<string, StatuteRef>();
  for (const claim of claims)
    for (const ref of claim.refs)
      if (!ref.via && ref.code) found.set(`${ref.code}.${ref.chapter}.${ref.section ?? ''}`, ref);
  return [...found.values()];
}

// ── The writer's claims ledger ────────────────────────────────────────────

/** What an imported article's writer recorded for one sentence: the page it leaned on and the passage it relied on. */
export interface WriterLedgerEntry {
  sentence: string;
  sourceUrl: string;
  supportingQuote: string;
}

const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** The ledger on a document output (`output.writerLedger`); empty when there is none. */
export function readWriterLedger(output: unknown): WriterLedgerEntry[] {
  const ledger = record(record(output).writerLedger);
  if (!Array.isArray(ledger.claims)) return [];
  return ledger.claims.map(record).flatMap((entry) =>
    typeof entry.sentence === 'string' &&
    typeof entry.sourceUrl === 'string' &&
    typeof entry.supportingQuote === 'string'
      ? [
          {
            sentence: entry.sentence,
            sourceUrl: entry.sourceUrl,
            supportingQuote: entry.supportingQuote,
          },
        ]
      : [],
  );
}

export type VerifiedLedgerEntry<T extends LedgerQuote = WriterLedgerEntry> = T & {
  /** The quote was found, verbatim, in the text Mora fetched for `sourceUrl`. Anything else is a claim, not evidence. */
  verified: boolean;
  /** Why it was not verified: the page was not read, or it does not contain the quote. */
  why: string | null;
};

/** What verification needs of an entry: the page and the passage. */
export interface LedgerQuote {
  sourceUrl: string;
  supportingQuote: string;
}

/**
 * The writer's quotes are checked, never trusted: an entry is verified only when its page is in the store
 * with text and its quote is a contiguous passage of that text (typography and whitespace aside).
 */
export function verifyLedger<T extends LedgerQuote>(
  entries: readonly T[],
  index: EvidenceIndex,
): VerifiedLedgerEntry<T>[] {
  return entries.map((entry) => {
    const page = lookupEvidence(index, entry.sourceUrl);
    if (!page?.ok || !page.text?.trim())
      return {
        ...entry,
        verified: false,
        why: 'The page this quote is attributed to was not read.',
      };
    if (entry.supportingQuote.trim().length < MIN_QUOTE_CHARS)
      return { ...entry, verified: false, why: 'The quote is too short to prove anything.' };
    return quoteIsInExcerpt(entry.supportingQuote, stripLinks(page.text))
      ? { ...entry, verified: true, why: null }
      : { ...entry, verified: false, why: 'The quote is not in the page text Mora fetched.' };
  });
}

function loose(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9$%.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** The ledger entry for a claim's sentence: the two are the same sentence, or one holds the other. */
export function ledgerEntryFor<T extends WriterLedgerEntry>(
  sentence: string,
  ledger: readonly T[],
): T | null {
  const target = loose(sentence);
  if (target.length < 20) return null;
  return (
    ledger.find((entry) => {
      const candidate = loose(entry.sentence);
      return candidate.length >= 20 && (candidate.includes(target) || target.includes(candidate));
    }) ?? null
  );
}

/**
 * The part of a long page a verified ledger quote points at, for a judge whose view of the page is cut. The
 * judge's own quote must still be found in the page; this only makes sure the passage the writer relied on
 * is in front of it.
 */
export function pageViewFor(
  text: string,
  anchors: readonly string[],
  maxChars: number,
): { text: string; truncated: boolean } {
  const cleaned = stripLinks(text);
  if (cleaned.length <= maxChars) return { text: cleaned, truncated: false };
  const head = Math.floor(maxChars / 2);
  const parts = [cleaned.slice(0, head)];
  let left = maxChars - head;
  for (const anchor of anchors) {
    if (left < 400) break;
    const at = cleaned.indexOf(anchor.trim());
    if (at < head) continue;
    const from = Math.max(head, at - 300);
    const slice = cleaned.slice(
      from,
      Math.min(cleaned.length, from + Math.min(left, anchor.length + 800)),
    );
    parts.push(slice);
    left -= slice.length + 7;
  }
  if (parts.length === 1) return { text: cleaned.slice(0, maxChars), truncated: true };
  return { text: parts.join('\n[...]\n'), truncated: true };
}
